import CURRENCIES from '@/constants/currency'
import store from '@/store/store'

/**
 * Formate un prix avec une devise spécifique
 * @param {number} number - Le montant à formater
 * @param {string} currencyCode - Le code de la devise (ex: 'EUR', 'USD')
 * @param {string} lang - La langue pour le formatage (par défaut: 'fr')
 * @param {number} minScale - Nombre minimum de décimales (par défaut: 2)
 * @param {number} maxScale - Nombre maximum de décimales (par défaut: 2)
 * @returns {string} Le prix formaté avec la devise
 */
export const formatPriceWithCurrency = (number, currencyCode = null, lang = 'fr', minScale = 2, maxScale = 2) => {
  const nb = (!Number.isNaN(number) && number !== undefined ? number : 0)

  // Si currencyCode n'est pas fourni, on utilise la devise par défaut de l'app
  const currency = currencyCode || 'EUR'

  return new Intl.NumberFormat(lang, {
    style: 'currency',
    currency,
    currencyDisplay: 'symbol',
    minimumFractionDigits: minScale,
    maximumFractionDigits: maxScale,
  }).format(nb)
}

/**
 * Obtient le symbole d'une devise à partir de son code
 * @param {string} currencyCode - Le code de la devise
 * @returns {string} Le symbole de la devise
 */
export const getCurrencySymbol = currencyCode => {
  if (!currencyCode) return '€'

  const currency = CURRENCIES[currencyCode]
  return currency ? currency.symbol : currencyCode
}

/**
 * Obtient le nom long d'une devise à partir de son code
 * @param {string} currencyCode - Le code de la devise
 * @returns {string} Le nom long de la devise
 */
export const getCurrencyLongName = currencyCode => {
  if (!currencyCode) return 'Euro'

  const currency = CURRENCIES[currencyCode]
  return currency ? currency.long : currencyCode
}

/**
 * @param {string} unit - L'unité de mesure (ex: 'epl')
 * @param {string} currencyCode - Le code de la devise (ex: 'EUR', 'USD')
 * @returns {string} L'unité avec la devise appropriée
 */
export const getMeasurementUnitWithCurrency = (unit, currencyCode = null) => {
  const MEASUREMENT = {
    metric: {
      epl: { abbr: '€/L', long: 'currency_per_liter' },
    },
    imperial: {
      epl: { abbr: '€/USG', long: 'currency_per_gallon' },
    },
  }

  const system = store.state.appConfig.measurement || 'metric'
  const measurementUnit = MEASUREMENT[system]?.[unit]

  if (!measurementUnit) return ''

  let result = measurementUnit.abbr

  // Si c'est une unité de prix (epl), on remplace la devise
  if (unit === 'epl' && currencyCode) {
    const currency = CURRENCIES[currencyCode]
    const currencySymbol = currency ? currency.symbol : currencyCode
    result = result.replace(/^./, currencySymbol)
  }

  return result
}

export default formatPriceWithCurrency
